import { HudHint, HudHintStatus, HudMessage } from '../../services/hud';

export const HINT_KINDS = ['decision', 'coverage', 'pacing', 'contradiction', 'followup', 'build', 'message', 'other'] as const;
export type KnownKind = typeof HINT_KINDS[number];

export const KIND_STYLE: Record<KnownKind, { label: string; badge: string }> = {
  decision:      { label: 'Decision',      badge: 'bg-blue-50 text-blue-700 border-blue-200' },
  coverage:      { label: 'Coverage',      badge: 'bg-amber-50 text-amber-700 border-amber-200' },
  pacing:        { label: 'Pacing',        badge: 'bg-orange-50 text-orange-700 border-orange-200' },
  contradiction: { label: 'Contradiction', badge: 'bg-rose-50 text-rose-700 border-rose-200' },
  followup:      { label: 'Follow-up',     badge: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  build:         { label: 'Build',         badge: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  message:       { label: 'Message',       badge: 'bg-purple-50 text-purple-700 border-purple-200' },
  other:         { label: 'Other',         badge: 'bg-slate-50 text-slate-600 border-slate-200' },
};

export const HINT_STATUS_STYLE: Record<HudHintStatus, { label: string; pill: string }> = {
  active:    { label: 'Active',              pill: 'bg-primary/10 text-primary' },
  accepted:  { label: 'Accepted',            pill: 'bg-emerald-100 text-emerald-700' },
  confirmed: { label: 'Confirmed',           pill: 'bg-blue-100 text-blue-700' },
  ignored:   { label: 'Ignored',             pill: 'bg-slate-100 text-slate-500' },
  resolved:  { label: 'Covered by the room', pill: 'bg-teal-50 text-teal-700' },
  expired:   { label: 'Expired',             pill: 'bg-slate-100 text-slate-400' },
};

export const PRIORITY_RANK: Record<string, number> = { high: 0, normal: 1, low: 2 };

export function normaliseKind(kind: string): KnownKind {
  return (HINT_KINDS as readonly string[]).includes(kind) ? (kind as KnownKind) : 'other';
}

export function isBuildQuestion(hint: HudHint): boolean {
  return hint.hintId.startsWith('bq-');
}

export function toMs(iso?: string | null): number {
  if (!iso) return 0;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? 0 : t;
}

export function formatTime(iso?: string | null): string {
  const t = toMs(iso);
  if (!t) return '';
  return new Date(t).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

export function formatCountdown(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export function errorMessage(err: any, fallback: string): string {
  if (err?.response?.status === 403) return 'You need write access to this session.';
  return err?.response?.data?.error || err?.message || fallback;
}

/** Waiting on the server and not yet past its expiry on this clock. */
export function isWaiting(m: HudMessage, now: number): boolean {
  return m.status === 'waiting' && toMs(m.expiresAt) > now;
}

export type MessageOutcome = 'yes' | 'no' | 'expired';

export function messageOutcome(m: HudMessage): MessageOutcome {
  if (m.status === 'yes' || m.response === 'yes') return 'yes';
  if (m.status === 'no' || m.response === 'no') return 'no';
  return 'expired';
}

/** Href to another tab of the current session page, keeping the other query params. */
export function tabHref(search: string, tab: string, extra: Record<string, string> = {}): string {
  const params = new URLSearchParams(search);
  params.set('tab', tab);
  for (const [k, v] of Object.entries(extra)) params.set(k, v);
  return `?${params.toString()}`;
}
